import {
  ChatOutlined,
  DeleteOutlineOutlined,
  FileDownloadOutlined,
  MoreVert,
  OpenInNew,
  Refresh,
} from "@mui/icons-material";
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Popover,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import DeleteDialog from "src/reusable/components/DeleteDialog";
import DocumentStatusIndicator from "src/reusable/components/DocumentStatusIndicator";
import { NucToast } from "src/reusable/components/Toast";
import {
  useDownloadDocumentPreview,
  useGetSDRArtifacts,
} from "../services/DocumentServices";
import {
  useDeleteSDRById,
  useReprocessSDRById,
} from "../services/DatasetServices";
import { useCanDeleteDatasetRecords } from "./useCanDeleteDatasetRecords";
import { useCanReprocessDatasetRecords } from "./useCanReprocessDatasetRecords";
import { ProcessingStatusData, SourceDataRecord } from "../types";
import { SDRImage } from "./SDRImage";
import { SourceMetadataSummary } from "./SourceMetadata";
import { useChatStore } from "src/views/assistant/stores/ChatStore";

export interface SDRCardProps {
  record: SourceDataRecord;
  onRowClick: (record: SourceDataRecord) => void;
  isDocumentDataset: boolean;
  /** Card variant - compact for mobile, comfortable for tablet */
  variant?: "compact" | "comfortable";
  /** Processing status from API for detailed display */
  processingStatus?: ProcessingStatusData;
}

const SDRCard = ({
  record,
  onRowClick,
  isDocumentDataset,
  variant = "comfortable",
  processingStatus,
}: SDRCardProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const history = useHistory();
  const { setSourceDataRecordIds, addUploadedFile } = useChatStore();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [reprocessDialogOpen, setReprocessDialogOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [thumbnailAnchorEl, setThumbnailAnchorEl] =
    useState<null | HTMLElement>(null);

  // Auto-select variant based on screen size if not explicitly set
  const effectiveVariant = isMobile ? "compact" : variant;

  const {
    mutate: deleteSDRById,
    isLoading: isDeleteLoading,
    isSuccess: isDeleteSuccess,
    isError: isDeleteError,
  } = useDeleteSDRById();

  const canDeleteSdr = useCanDeleteDatasetRecords(record.dataset_fk);
  // Same two-axis gate the delete beside it already uses (frontend#2254):
  // reprocess rebuilds the document's chunks and embeddings, so the backend
  // requires write on this dataset and an ungated item always 403s.
  const canReprocessSdr = useCanReprocessDatasetRecords(record.dataset_fk);

  const {
    mutate: reprocessSDR,
    isLoading: isReprocessLoading,
    isSuccess: isReprocessSuccess,
    reset: resetReprocess,
  } = useReprocessSDRById();

  const { data: artifacts } = useGetSDRArtifacts(record.id);
  const artifactId =
    artifacts && artifacts.length > 0 ? artifacts[0]?.id : null;
  const { refetch: downloadDocument } = useDownloadDocumentPreview(artifactId);

  useEffect(() => {
    if (isDeleteSuccess) {
      NucToast({
        message: "Deleted Dataset record",
        type: "success",
      });
    } else if (isDeleteError) {
      NucToast({
        message: "Error deleting Dataset record",
        type: "error",
      });
    }
  }, [isDeleteSuccess, isDeleteError]);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDownload = async (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (!artifactId) return;

    setIsDownloading(true);
    handleMenuClose();
    try {
      const { data } = await downloadDocument();
      if (data) {
        const url = URL.createObjectURL(data);
        const fileName = record.data.file_name || `document-${record.id}.pdf`;
        const a = document.createElement("a");
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error("Error downloading document:", error);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleDeleteClick = () => {
    handleMenuClose();
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    deleteSDRById(record.id);
    setDeleteDialogOpen(false);
  };

  const handleReprocessClick = () => {
    handleMenuClose();
    resetReprocess(); // Reset mutation state so dialog can open again after previous success
    setReprocessDialogOpen(true);
  };

  const handleReprocessConfirm = () => {
    reprocessSDR(record.id);
    setReprocessDialogOpen(false);
  };

  const handleChatWithDocument = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    const fileName = record.data?.file_name || `Document ${record.id}`;

    // Set the source data record IDs for document-scoped search
    setSourceDataRecordIds([record.id]);

    // Add the document to uploadedFiles for UI display (shows as attachment chip)
    addUploadedFile({
      id: `sdr-${record.id}`,
      file: {
        name: fileName,
        size: 0,
        type: "application/pdf",
      } as File,
      datasetId: record.dataset_fk,
      sourceDataRecordId: record.id,
      uploadStatus: "uploaded",
      uploadProgress: 100,
      processingStatus: "completed",
      processingProgress: 100,
    });

    handleMenuClose();

    // Navigate to AtomAssist
    history.push({
      pathname: "/assistant",
      state: {
        preloadDocument: {
          sdrId: record.id,
          fileName: fileName,
          datasetId: record.dataset_fk,
        },
      },
    });
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const handleThumbnailHover = (event: React.MouseEvent<HTMLElement>) => {
    setThumbnailAnchorEl(event.currentTarget);
  };

  const handleThumbnailLeave = () => {
    setThumbnailAnchorEl(null);
  };

  // Get display info from record
  const fileName = record.data?.file_name || `Record ${record.id}`;
  const title = record.data?.extracted_metadata?.title;
  const description = record.data?.extracted_metadata?.description;
  const author = record.data?.extracted_metadata?.author;
  const docType = record.data?.extracted_metadata?.document_type;
  const pubDate = record.data?.extracted_metadata?.publication_date;
  const createDate = formatDate(record.create_date);

  // Sizing based on variant - BIGGER thumbnails per feedback
  const thumbnailSize =
    effectiveVariant === "compact"
      ? { width: 80, minHeight: 110 }
      : { width: 120, minHeight: 160 };

  const cardPadding =
    effectiveVariant === "compact" ? { py: 1.5, px: 2 } : { py: 2, px: 2.5 };

  return (
    <>
      <Card
        data-testid={`sdr-card-${record.id}`}
        sx={{
          mb: 1.5,
          borderRadius: 2,
          boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
          "&:hover": {
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
          },
          "&:active": {
            boxShadow: "0 2px 6px rgba(0,0,0,0.16)",
          },
        }}
      >
        <CardActionArea
          onClick={() => onRowClick(record)}
          sx={{ display: "flex", alignItems: "stretch" }}
        >
          {/* Thumbnail for document datasets */}
          {isDocumentDataset && (
            <Box
              onMouseEnter={handleThumbnailHover}
              onMouseLeave={handleThumbnailLeave}
              sx={{
                ...thumbnailSize,
                bgcolor: grey[100],
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                cursor: "zoom-in",
              }}
            >
              <SDRImage sdrID={record.id} />
            </Box>
          )}

          {/* Content */}
          <CardContent
            sx={{
              flex: 1,
              ...cardPadding,
              "&:last-child": { pb: cardPadding.py },
              minWidth: 0,
            }}
          >
            {/* File name */}
            <Typography
              variant="subtitle2"
              data-testid="sdr-filename"
              sx={{
                fontWeight: 600,
                mb: 0.25,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {fileName}
            </Typography>

            {/* Title if available */}
            {title && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  mb: 0.25,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  fontSize:
                    effectiveVariant === "compact" ? "0.8rem" : "0.875rem",
                }}
              >
                {title}
              </Typography>
            )}

            {/* Description - 2 lines max */}
            {description && effectiveVariant === "comfortable" && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  mb: 0.5,
                }}
              >
                {description}
              </Typography>
            )}

            {/* Metadata - nicely formatted */}
            <Stack spacing={0.5} sx={{ mt: 1 }}>
              {/* Author and Type row */}
              {effectiveVariant === "comfortable" && (author || docType) && (
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  flexWrap="wrap"
                >
                  {author && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontSize: "0.85rem" }}
                    >
                      {author}
                    </Typography>
                  )}
                  {author && docType && (
                    <Typography variant="body2" color="text.disabled">
                      ·
                    </Typography>
                  )}
                  {docType && (
                    <Chip
                      label={docType}
                      size="small"
                      sx={{
                        height: 22,
                        fontSize: "0.75rem",
                        bgcolor: grey[100],
                        color: grey[700],
                      }}
                    />
                  )}
                </Stack>
              )}

              <SourceMetadataSummary data={record.data} recordId={record.id} />

              {/* Date and Status row */}
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="caption" color="text.secondary">
                  {pubDate || createDate}
                </Typography>
                <DocumentStatusIndicator
                  recordId={record.id}
                  chunksWithEmbedding={record.chunks_with_embedding}
                  processingStatus={processingStatus}
                  variant={effectiveVariant === "compact" ? "compact" : "full"}
                  showChunkProgress={effectiveVariant !== "compact"}
                />
              </Stack>
            </Stack>

            {/* Quick actions - visible in comfortable variant */}
            {effectiveVariant === "comfortable" && isDocumentDataset && (
              <Stack
                direction="row"
                spacing={0.5}
                sx={{ mt: 1 }}
                onClick={(e) => e.stopPropagation()}
              >
                <Tooltip title="Chat with Document">
                  <IconButton
                    size="small"
                    onClick={handleChatWithDocument}
                    data-testid={`sdr-btn-quick-chat-${record.id}`}
                    sx={{
                      minWidth: 44,
                      minHeight: 44,
                      color: "primary.main",
                    }}
                  >
                    <ChatOutlined fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Download">
                  <IconButton
                    size="small"
                    onClick={handleDownload}
                    disabled={isDownloading || !artifactId}
                    sx={{
                      minWidth: 44,
                      minHeight: 44,
                    }}
                  >
                    <FileDownloadOutlined fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            )}
          </CardContent>

          {/* Menu button - always visible */}
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              pt: 1,
              pr: 0.5,
            }}
          >
            <IconButton
              size="medium"
              onClick={handleMenuOpen}
              data-testid={`sdr-action-menu-${record.id}`}
              sx={{
                minWidth: 44,
                minHeight: 44,
                color: "text.secondary",
              }}
            >
              <MoreVert />
            </IconButton>
          </Box>
        </CardActionArea>
      </Card>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem
          onClick={() => {
            handleMenuClose();
            onRowClick(record);
          }}
          data-testid={`sdr-menu-open-${record.id}`}
          sx={{ minHeight: 44 }}
        >
          <ListItemIcon>
            <OpenInNew fontSize="small" />
          </ListItemIcon>
          <ListItemText>Open</ListItemText>
        </MenuItem>
        {isDocumentDataset && (
          <MenuItem
            onClick={() => handleChatWithDocument()}
            data-testid="sdr-menu-chat-with-document"
            sx={{ minHeight: 44 }}
          >
            <ListItemIcon>
              <ChatOutlined fontSize="small" color="primary" />
            </ListItemIcon>
            <ListItemText>Chat with Document</ListItemText>
          </MenuItem>
        )}
        {isDocumentDataset && (
          <MenuItem
            onClick={() => handleDownload()}
            disabled={isDownloading || !artifactId}
            data-testid={`sdr-menu-download-${record.id}`}
            sx={{ minHeight: 44 }}
          >
            <ListItemIcon>
              <FileDownloadOutlined fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              {isDownloading ? "Downloading..." : "Download"}
            </ListItemText>
          </MenuItem>
        )}
        {isDocumentDataset && (
          <MenuItem
            onClick={handleReprocessClick}
            disabled={!canReprocessSdr || isReprocessLoading}
            data-testid={`sdr-menu-reprocess-${record.id}`}
            sx={{ minHeight: 44 }}
          >
            <ListItemIcon>
              <Refresh fontSize="small" color="warning" />
            </ListItemIcon>
            <ListItemText>
              {isReprocessLoading ? "Reprocessing..." : "Reprocess Document"}
            </ListItemText>
          </MenuItem>
        )}
        <MenuItem
          onClick={handleDeleteClick}
          // Requires the dataset:delete capability AND write on this
          // dataset — same two-axis decision the delete endpoint enforces.
          disabled={!canDeleteSdr}
          data-testid={`sdr-menu-delete-${record.id}`}
          sx={{ color: "error.main", minHeight: 44 }}
        >
          <ListItemIcon>
            <DeleteOutlineOutlined
              fontSize="small"
              color={canDeleteSdr ? "error" : "disabled"}
            />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Delete Dialog */}
      <DeleteDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        title="Delete Source Data Record"
        content="This is a destructive action. Are you sure you want to delete this source data record? This action cannot be undone."
        onDelete={handleDeleteConfirm}
        isLoading={isDeleteLoading}
        isSuccess={isDeleteSuccess}
        confirmButtonText="Delete Source Data Record"
      />

      {/* Reprocess Dialog */}
      <DeleteDialog
        open={reprocessDialogOpen}
        onClose={() => setReprocessDialogOpen(false)}
        title="Reprocess Document"
        content="This will clear all processed data (OCR, chunks, embeddings) and re-run the full processing pipeline. The document will be unavailable for search until processing completes."
        onDelete={handleReprocessConfirm}
        isLoading={isReprocessLoading}
        isSuccess={isReprocessSuccess}
        confirmButtonText="Reprocess"
      />

      {/* Thumbnail Preview Popover */}
      <Popover
        open={Boolean(thumbnailAnchorEl)}
        anchorEl={thumbnailAnchorEl}
        onClose={handleThumbnailLeave}
        anchorOrigin={{
          vertical: "center",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "center",
          horizontal: "left",
        }}
        disableRestoreFocus
        sx={{
          pointerEvents: "none",
        }}
        slotProps={{
          paper: {
            sx: {
              p: 1,
              boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
              borderRadius: 2,
            },
          },
        }}
      >
        <Box
          sx={{
            width: 300,
            height: 400,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: grey[50],
          }}
        >
          <SDRImage sdrID={record.id} />
        </Box>
      </Popover>
    </>
  );
};

export default SDRCard;
